import { Injectable, inject } from '@angular/core';
import { TenantDto } from '@nusign/picflow-backend-types';
import { Observable, map, tap } from 'rxjs';
import { GROWTHBOOK } from '../growthbook';
import { DataLoaderService } from './data-loader.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class SharedWithMeTenantService {
  private tenantService = inject(TenantService);
  private permissionService = inject(PermissionService);
  private state = inject(StateService);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private growthBook = inject(GROWTHBOOK);
  private storage = inject(StorageService);

  public loadSharedWithMeTenants(): Observable<TenantDto[]> {
    return this.tenantService.getTenants().pipe(
      map(resp => resp.shared_tenants),
      tap(tenants => {
        this.state.sharedWithMeTenants = tenants.filter(tenant => !this.state.tenants?.some(t => t.id === tenant.id));
        const defaultTenant = this.getDefaultTenant(true);
        if (defaultTenant) this.setActiveSharedWithMeTenant(defaultTenant);
      })
    );
  }

  public setActiveSharedWithMeTenant(tenant: TenantDto) {
    this.storage.remove('activeSharedWithMeTenant');
    this.storage.remove('backupSharedWithMeTenantId');
    this.storage.remove('activeTenantId');
    this.storage.remove('backupTenantId');

    if (tenant) {
      this.storage.set('activeSharedWithMeTenant', tenant.id, true);
      this.storage.set('backupSharedWithMeTenantId', tenant.id);
    }

    const activeTenant = this.state.activeTenant$.value;
    const sharedWithMeActiveTenant = this.state.sharedWithMeActiveTenant$.value;
    if (activeTenant || (sharedWithMeActiveTenant && sharedWithMeActiveTenant.id !== tenant?.id)) {
      this.loader.clear();
      this.observableLoader.clear();
    }
    this.state.sharedWithMeActiveTenant$.next(tenant);
    this.state.activeTenant$.next(undefined);
    this.state.activeTenantRole = undefined;
    this.permissionService.setRolePermissions(undefined);

    this.growthBook.setAttributes({
      ...this.growthBook.getAttributes(),
      tenantId: undefined,
    });
  }

  public getDefaultTenant(forAutoSelect: boolean): TenantDto {
    if (forAutoSelect) {
      if (this.state.activeTenant$.value) return;
      if (this.storage.get('activeTenantId') || this.storage.get('backupTenantId')) return;
    }
    const storageTenantId =
      this.storage.get('activeSharedWithMeTenant') || this.storage.get('backupSharedWithMeTenantId');
    if (storageTenantId) {
      const tenant = this.state.sharedWithMeTenants.find(t => t.id === storageTenantId);
      if (tenant) return tenant;
    }
    if (forAutoSelect) return;
    if (this.state.sharedWithMeTenants.length === 1) return this.state.sharedWithMeTenants[0];
  }
}
